import React, { useState, useMemo, useCallback } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import dayjs from 'dayjs';
import GPSFiltersBaseRow from './GPSFiltersBaseRow';
import GPSAdvancedFiltersBar from './GPSAdvancedFiltersBar';
import GPSAdvancedFiltersPanel from './GPSAdvancedFiltersPanel';
import { getFilterLayout } from './gpsFilters.config';
import { isSenseActiveForTab, getForcedSense } from '../../utils/gpsSense';

const GPSSelectionHeader = ({ jail = false, activeTab = 'general' }) => {
  const gps = useGPS();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const senseActive = isSenseActiveForTab(activeTab);
  const forcedSense = getForcedSense(activeTab);

  const selectedFilters = [
    gps.selectedShopSigns,
    gps.selectedFurnitures,
    gps.selectedSectors,
    gps.selectedBrands,
    gps.selectedProblems,
    gps.selectedStatus,
    gps.selectedTierings,
    gps.selectedRequestTypes,
    gps.selectedShops,
    gps.selectedCustomFilters,
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hasFiltersApplied = useMemo(() => selectedFilters.some((f) => f?.length > 0), selectedFilters);

  // Advanced groups = the filters actually rendered in the advanced panel
  // (dynamic, per the layout) + the custom (compliance) filters.
  const { advanced } = getFilterLayout(gps, jail);
  const advancedGroups = [
    ...advanced.map((f) => ({ valueKey: f.valueKey, label: f.label })),
    { valueKey: 'selectedCustomFilters', label: 'Filtres personnalisés' },
  ];
  const activeAdvancedGroups = advancedGroups.filter((g) => (gps[g.valueKey] || []).length > 0);
  const advancedActiveCount = activeAdvancedGroups.length;
  const advancedPreview = advancedActiveCount > 0
    ? activeAdvancedGroups.map((g) => g.label).join(' · ')
    : advancedGroups.map((g) => g.label).join(' · ');

  const resetFilters = useCallback(() => {
    gps.setSelectedShopSigns([]);
    gps.setSelectedFurnitures([]);
    gps.setSelectedSectors([]);
    gps.setSelectedBrands([]);
    gps.setSelectedProblems([]);
    gps.setSelectedStatus([]);
    gps.setSelectedTierings([]);
    gps.setSelectedRequestTypes([]);
    gps.setSelectedShops([]);
    gps.setSelectedCustomFilters([]);
    // Note: `sense` is a view mode, not a filter — it is intentionally not reset.
  }, [gps]);

  return (
    <div className="mb-3 relative z-15">
      <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
        {/* Header */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-900">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex items-center justify-center rounded-md w-6 h-6 bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Filtres</span>
              {gps.selectedProjet && gps.lastSyncDate && (
                <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-1.5 truncate hidden sm:inline">
                  Sync. {dayjs(gps.lastSyncDate).format('DD/MM/YYYY HH:mm')}
                </span>
              )}
            </div>
            {hasFiltersApplied && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  resetFilters();
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Filters body */}
        <div className="px-4 py-3">
          <GPSFiltersBaseRow jail={jail} senseDisabled={!senseActive} senseValueOverride={forcedSense} />
          <GPSAdvancedFiltersBar
            expanded={showAdvanced}
            onToggle={() => setShowAdvanced((v) => !v)}
            activeCount={advancedActiveCount}
            preview={advancedPreview}
          />
          {showAdvanced && (
            <GPSAdvancedFiltersPanel
              jail={jail}
              onReset={resetFilters}
              onApply={() => setShowAdvanced(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GPSSelectionHeader;
